// Mounts Stripe's Express Checkout Element (Apple Pay + Google Pay + Link).
// React 15 class component — imperative mount because @stripe/react-stripe-js
// requires React >= 16.8 (we are on 15.6.2). Coexists on the same page as the
// legacy react-stripe-elements@6 card form (StripeCheckoutForm.js); both share
// Stripe's global window.Stripe so only one Stripe.js bundle is loaded.
//
// Active checkout page: frontend/src/views/Cart/Checkout.js (mounted from
// routes.js line ~790 at path /checkout).
//
// Props:
//   stripeInstance    object  A ready Stripe instance (window.Stripe(key)),
//                             passed down from Checkout.js this.state.stripe.
//                             The wallet reuses the same instance the card form
//                             already created — no second instance, no loader.
//   amount            int     amount in cents
//   currency          string  e.g. 'usd'
//   userCart          array   same shape sent to /createOrder
//   paymentInterval   string|null  null for one-time; otherwise subscription
//   customerEmail     string  used for Link pre-fill
//   apiBaseUrl        string  e.g. https://api.app.analystprep.com (prefix
//                             prepended to /api/checkout_v1/...)
//   authHeader        string  Authorization header value used by other
//                             checkout calls in the parent component
//   onSuccess(orderId)
//   onError(err)

import React, { Component } from 'react';
import axios from 'axios';
import { isLocalhost } from '../../../utils/CommonUtils';

// Single source of truth for the wallet-button visibility kill switch.
// Flip to false and redeploy frontend to remove all wallet UI without
// touching backend code. The card form remains the only visible payment option.
const WALLET_BUTTONS_ENABLED = true;

class ExpressCheckoutWallets extends Component {
  constructor(props) {
    super(props);
    this.state = { available: null, error: null };
    this._mountRef = null;
    this._stripe = null;
    this._elements = null;
    this._element = null;
    this._destroyed = false;
    this._handleConfirm = this._handleConfirm.bind(this);
    this._setMountRef = this._setMountRef.bind(this);
  }

  componentDidMount() {
    if (!WALLET_BUTTONS_ENABLED) return;
    // if (isLocalhost) return; // mock buttons only — skip Stripe init
    this._init();
  }

  componentDidUpdate(prevProps) {
    // The cart total can change after mount (coupon applied/removed, item
    // edited). Stripe's deferred Elements need elements.update({ amount })
    // so the wallet sheet shows the correct total — otherwise it pays the
    // stale amount. Only update for a positive integer amount.
    if (!this._elements) return;
    const amt = this.props.amount;
    if (amt !== prevProps.amount && typeof amt === 'number' && amt > 0) {
      try {
        this._elements.update({ amount: amt });
      } catch (e) {
        console.warn('ExpressCheckoutWallets: elements.update(amount) failed', e && e.message);
      }
    }
  }

  componentWillUnmount() {
    this._destroyed = true;
    try {
      if (this._element && typeof this._element.destroy === 'function') {
        this._element.destroy();
      }
    } catch (e) {
      // ignore — element may already be unmounted
    }
  }

  _init() {
    const self = this;
    // Reuse the ready Stripe instance the checkout page already created
    // (Checkout.js this.state.stripe). The wallet only renders inside the
    // `stripe ? <StripeProvider stripe={stripe}>` block, so the instance is
    // guaranteed present here — no async load, no second instance.
    const stripe = this.props.stripeInstance;
    if (this._destroyed || !stripe) {
      if (!stripe) {
        console.warn('ExpressCheckoutWallets: no stripeInstance prop — skipping mount');
      }
      return;
    }
    // Stripe's elements({ amount }) throws if amount <= 0. The parent gates
    // rendering on a positive amount, but guard here too in case this mounts
    // before the cart total is known.
    const amount = this.props.amount;
    if (typeof amount !== 'number' || !(amount > 0)) {
      console.warn('ExpressCheckoutWallets: amount not positive yet — skipping mount', amount);
      return;
    }
    try {
      self._stripe = stripe;

      const isSubscription = !!self.props.paymentInterval;
      const elements = stripe.elements({
        mode: isSubscription ? 'subscription' : 'payment',
        amount: self.props.amount,
        currency: (self.props.currency || 'usd').toLowerCase(),
        paymentMethodCreation: 'manual',
      });
      self._elements = elements;

      // `paymentMethods` is the current option (replaces the deprecated
      // `wallets`). applePay/googlePay accept 'always' | 'auto' | 'never';
      // link accepts 'auto' | 'never' (no 'always'). 'auto' lets Link surface
      // when the account has it enabled and the buyer is eligible.
      const ece = elements.create('expressCheckout', {
        buttonType: { applePay: 'buy', googlePay: 'buy' },
        paymentMethods: {
          applePay: 'always',
          googlePay: 'always',
          link: 'auto',
        },
      });
      self._element = ece;

      ece.on('ready', function (e) {
        if (self._destroyed) return;
        const apm = e && e.availablePaymentMethods;
        const any = apm && (apm.applePay || apm.googlePay || apm.link);
        self.setState({ available: !!any });
      });

      ece.on('confirm', self._handleConfirm);

      if (self._mountRef) {
        ece.mount(self._mountRef);
      }
    } catch (err) {
      console.error('ExpressCheckoutWallets mount failed', err);
      self.setState({ error: (err && err.message) || 'Wallet load failed' });
    }
  }

  _handleConfirm(event) {
    const self = this;
    const p = self.props;
    const apiBase = p.apiBaseUrl || '';

    (function run() {
      return self._elements.submit().then(function (submitRes) {
        if (submitRes && submitRes.error) throw submitRes.error;

        return self._stripe.createPaymentMethod({
          elements: self._elements,
          params: { billing_details: { email: p.customerEmail } },
        });
      }).then(function (pmRes) {
        if (pmRes.error || !pmRes.paymentMethod) {
          throw pmRes.error || new Error('PaymentMethod creation failed');
        }
        const pm = pmRes.paymentMethod;
        const walletType = (pm && pm.card && pm.card.wallet && pm.card.wallet.type)
          || (event && event.expressPaymentType)
          || null;

        const body = {
          payment_type: 'stripe',
          amount: p.amount / 100,
          userCart: p.userCart,
          paymentInterval: p.paymentInterval || null,
          paymentMethodId: pm.id,
          walletType: walletType,
        };
        const headers = { Authorization: p.authHeader || '' };

        return axios.post(apiBase + '/checkout_v1/createOrder', body, { headers: headers })
          .then(function (resp) { return resp.data; });
      }).then(function (data) {
        // Backend (createOrder) success shape: { success: true, order_id, ... }
        // — matches the existing card/PayPal contract. Synchronous success
        // (no SCA): fulfilled inline, order_id returned.
        if (data && data.success && data.order_id) {
          // Express Checkout Element has no event.complete() — on success we
          // just navigate away via onSuccess.
          if (p.onSuccess) p.onSuccess(data.order_id);
          return null;
        }
        // SCA required: backend returns { requiresAction, clientSecret } and
        // has NOT fulfilled — clear the challenge then call finalizeWalletOrder.
        if (data && data.requiresAction && data.clientSecret) {
          return self._stripe.confirmPayment({
            clientSecret: data.clientSecret,
            redirect: 'if_required',
          }).then(function (cp) {
            if (cp && cp.error) throw cp.error;
            const pi = cp && cp.paymentIntent;
            if (!pi || pi.status !== 'succeeded') {
              throw new Error('Payment authentication did not complete');
            }
            const headers = { Authorization: p.authHeader || '' };
            return axios.post(apiBase + '/checkout_v1/finalizeWalletOrder', {
              paymentIntentId: pi.id,
              userCart: p.userCart,
              paymentInterval: p.paymentInterval || null,
            }, { headers: headers }).then(function (resp) { return resp.data; });
          }).then(function (finalizeData) {
            // finalizeWalletOrder returns the same { success: true, order_id } shape.
            if (finalizeData && finalizeData.success && finalizeData.order_id) {
              if (p.onSuccess) p.onSuccess(finalizeData.order_id);
              return null;
            }
            throw new Error('Finalize failed');
          });
        }
        throw new Error((data && data.message) || 'Unexpected response from createOrder');
      }).catch(function (err) {
        console.error('Wallet confirm flow failed', err);
        // Express Checkout Element: signal failure so the button resets.
        try { event.paymentFailed({ reason: 'fail' }); } catch (e) { }
        if (p.onError) p.onError(err);
      });
    })();
  }

  _setMountRef(el) {
    this._mountRef = el;
  }

  render() {
    if (!WALLET_BUTTONS_ENABLED) return null;

    if (isLocalhost) {
      const btn = (bg, color, label, key) => (
        <button
          key={key}
          type="button"
          style={{
            display: 'block',
            width: '100%',
            height: 44,
            margin: '0 0 8px 0',
            border: 'none',
            borderRadius: 6,
            background: bg,
            color: color,
            fontSize: 16,
            fontWeight: 600,
            cursor: 'pointer',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
          }}
          onClick={(e) => { e.preventDefault(); console.info('[isLocalhost] mock button clicked — no real flow:', label); }}
        >
          {label}
        </button>
      );
      return (
        <div className="chk_wallet_row chk_wallet_row__loading" style={{ margin: '0 0 16px 0' }}>
          <div style={{ fontSize: 11, color: '#999', textAlign: 'center', margin: '0 0 8px 0' }}>
            DEV PREVIEW — these buttons are visual mocks only
          </div>
          {btn('#000', '#fff', ' Pay', 'apple')}
          {btn('#1a73e8', '#fff', 'Buy with G Pay', 'google')}
          {btn('#00d66f', '#011e0f', 'Pay with Link', 'link')}
          {/* <div className="chk_wallet_divider">
            <span>OR PAY ANOTHER WAY</span>
          </div> */}
        </div>
      );
    }

    if (this.state.available === false) return null;

    const loading = this.state.available === null;
    return (
      <div className={"chk_wallet_row " + (loading ? "chk_wallet_row__loading" : "")}>
        <div ref={this._setMountRef} />
        {/* <div className="chk_wallet_divider">
          <span>OR PAY ANOTHER WAY</span>
        </div> */}
      </div>
    );
  }
}

export default ExpressCheckoutWallets;



// WEBPACK FOOTER //
// ./src/components/CheckoutComponents/PaymentMethods/ExpressCheckoutWallets.js